// src/Account.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Button, Alert, Divider, Chip, CircularProgress } from '@mui/material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from './AuthContext';
import { useAccess } from './useAccess';
import { auth } from './firebase';

const Account = () => {
  const { currentUser, userDoc } = useAuth();
  const { access, accessLabel, canUsePaidFeatures, loading, openPaywall } = useAccess();

  const location = useLocation();
  const [banner, setBanner] = useState(null);

  const [pwStatus, setPwStatus] = useState({ type: '', msg: '' });
  const [pwSending, setPwSending] = useState(false);

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  // ✅ Makes checkout click feel instant here too
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      setBanner({
        type: 'success',
        text: 'Success — your subscription setup is complete. Access will update shortly.',
      });
    } else if (params.get('canceled') === 'true') {
      setBanner({ type: 'info', text: 'Checkout canceled — no changes were made.' });
    } else {
      setBanner(null);
    }
  }, [location.search]);

  const statusChip = useMemo(() => {
    const a = String(access || 'locked').toLowerCase();
    const label =
      a === 'lifetime'
        ? 'Lifetime'
        : a === 'active'
        ? 'Active'
        : a === 'trialing'
        ? 'Free trial'
        : a === 'beta'
        ? 'Beta'
        : a === 'past_due'
        ? 'Payment issue'
        : a === 'canceled'
        ? 'Canceled'
        : 'Preview';

    return <Chip label={label} />;
  }, [access]);

  const handleSendPasswordReset = useCallback(async () => {
    setPwStatus({ type: '', msg: '' });

    const email = currentUser?.email;
    if (!email) {
      setPwStatus({ type: 'error', msg: 'No email found for this account.' });
      return;
    }

    try {
      setPwSending(true);
      await sendPasswordResetEmail(auth, email);
      setPwStatus({
        type: 'success',
        msg: 'Password reset email sent. Check your inbox (and spam).',
      });
    } catch (e) {
      console.error(e);
      setPwStatus({ type: 'error', msg: 'Could not send reset email. Try again shortly.' });
    } finally {
      setPwSending(false);
    }
  }, [currentUser]);

  const handleStartTrial = useCallback(async () => {
    if (checkoutLoading) return;
    try {
      setCheckoutLoading(true);
      await openPaywall(); // redirects on success
    } finally {
      setCheckoutLoading(false);
    }
  }, [checkoutLoading, openPaywall]);

  const openBillingPortal = useCallback(async () => {
    setPortalError('');
    if (!currentUser) {
      window.location.href = '/login';
      return;
    }

    if (String(access).toLowerCase() === 'lifetime') {
      setPortalError('This account has lifetime access — no subscription to manage.');
      return;
    }

    if (!userDoc?.stripeCustomerId) {
      setPortalError('No subscription found yet. Start a free trial first.');
      return;
    }

    try {
      setPortalLoading(true);
      const token = await currentUser.getIdToken();

      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to open billing portal');

      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      setPortalError(e?.message || 'Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  }, [currentUser, access, userDoc]);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
        Account
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Manage access, billing, and security settings.
      </Typography>

      {banner && (
        <Alert severity={banner.type} sx={{ mb: 2 }}>
          {banner.text}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>Access</Typography>
          {statusChip}
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {loading ? 'Checking access…' : accessLabel}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Signed in as</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {currentUser?.email || '—'}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 800, mb: 1 }}>Billing</Typography>

        {portalError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {portalError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {!canUsePaidFeatures && (
            <Button
              variant="contained"
              onClick={handleStartTrial}
              disabled={checkoutLoading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
            >
              {checkoutLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Opening checkout…
                </>
              ) : (
                'Start Free Trial'
              )}
            </Button>
          )}

          {canUsePaidFeatures && String(access).toLowerCase() !== 'lifetime' && (
            <Button
              variant="outlined"
              onClick={openBillingPortal}
              disabled={portalLoading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
            >
              {portalLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Opening…
                </>
              ) : (
                'Manage Subscription'
              )}
            </Button>
          )}
        </Box>

        {String(access).toLowerCase() === 'lifetime' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            You have lifetime access — you’ll never be charged.
          </Alert>
        )}

        {String(access).toLowerCase() === 'past_due' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            There’s a payment issue on your subscription. Use “Manage Subscription” to update payment details.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 800, mb: 1 }}>Security</Typography>

        {pwStatus.msg && (
          <Alert severity={pwStatus.type || 'info'} sx={{ mb: 1.5 }}>
            {pwStatus.msg}
          </Alert>
        )}

        <Button
          variant="outlined"
          onClick={handleSendPasswordReset}
          disabled={pwSending || !currentUser}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
        >
          {pwSending ? 'Sending…' : 'Change Password'}
        </Button>
      </Paper>
    </Box>
  );
};

export default Account;

